import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billingApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  CreditCard,
  Check,
  Zap,
  Building2,
  Users,
  HardHat,
  Crown,
  Loader2,
  AlertCircle,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_FEATURES = {
  trial: {
    icon: Clock,
    color: 'bg-purple-600',
    description: '30-day free trial with full Business features',
    features: [
      'Full CRM functionality',
      'Unlimited leads & projects',
      'Unlimited team members',
      'Custom branding',
      'Client portal access',
      'Document storage',
      'Calendar & Gmail sync',
    ],
  },
  base: {
    icon: Building2,
    color: 'bg-blue-600',
    description: 'Perfect for solo contractors',
    features: [
      'Unlimited leads & projects',
      'Client portal',
      'Document management',
      'Email reminders',
      'Add managers at $9/each',
    ],
  },
  pro: {
    icon: Zap,
    color: 'bg-orange-500',
    description: 'For growing teams',
    features: [
      'Everything in Base',
      '2 Project Managers included',
      '2 Forepersons included',
      'Priority support',
      'Advanced reporting',
    ],
  },
  business: {
    icon: Crown,
    color: 'bg-purple-600',
    description: 'For established companies',
    features: [
      'Everything in Pro',
      'Unlimited team members',
      'White-label client portal',
      'API access',
      'Dedicated support',
    ],
  },
};

export default function Billing() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [additionalManagers, setAdditionalManagers] = useState(0);

  useEffect(() => {
    fetchData();
    
    // Check for payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
      // Clean URL
      window.history.replaceState({}, '', '/billing');
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        billingApi.getSubscription(),
        billingApi.getPlans(),
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await billingApi.getCheckoutStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        fetchData();
      }
      // Clean URL
      window.history.replaceState({}, '', '/billing');
    } catch (error) {
      console.error('Failed to check payment status', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (user?.role !== 'owner') {
      toast.error('Only account owners can manage billing');
      return;
    }
    
    setCheckingOut(planId);
    try {
      const response = await billingApi.createCheckout({
        plan_id: planId,
        additional_managers: planId === 'base' ? additionalManagers : 0,
        origin_url: window.location.origin,
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-600">Active</Badge>;
      case 'trialing':
        return <Badge className="bg-blue-600">Trial</Badge>;
      case 'past_due':
        return <Badge className="bg-red-600">Past Due</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  if (user?.role !== 'owner') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md rounded-sm">
          <CardContent className="p-8 text-center">
            <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">Only account owners can manage billing and subscriptions.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription plan and billing</p>
      </div>

      {/* Current Plan */}
      <Card className="rounded-sm border-2 border-orange-500/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                Current Plan
              </CardTitle>
              <CardDescription>Your active subscription details</CardDescription>
            </div>
            {getStatusBadge(subscription?.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Plan</p>
              <p className="text-2xl font-bold">{subscription?.plan_name || 'Free Trial'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Project Managers</p>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-xl font-semibold">
                  {subscription?.managers_count || 0} / {subscription?.managers_allowed === -1 ? '∞' : subscription?.managers_allowed || 0}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Forepersons</p>
              <div className="flex items-center gap-2">
                <HardHat className="h-5 w-5 text-orange-500" />
                <span className="text-xl font-semibold">
                  {subscription?.forepersons_count || 0} / {subscription?.forepersons_allowed === -1 ? '∞' : subscription?.forepersons_allowed || 0}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {subscription?.status === 'trialing' ? 'Trial Ends' : 'Next Billing'}
              </p>
              <p className="text-xl font-semibold">
                {subscription?.trial_ends_at 
                  ? new Date(subscription.trial_ends_at).toLocaleDateString()
                  : subscription?.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : 'N/A'}
              </p>
            </div>
          </div>
          
          {subscription?.status === 'trialing' && (
            <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-950 rounded-sm flex items-start gap-3">
              <Crown className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-900 dark:text-purple-100">Full Business Access During Trial</p>
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  You have access to ALL Business features until {new Date(subscription.trial_ends_at).toLocaleDateString()}. 
                  Explore everything - unlimited team members, custom branding, and more!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans && Object.entries(plans).map(([planId, plan]) => {
            if (planId === 'trial') return null;
            
            const PlanIcon = PLAN_FEATURES[planId]?.icon || Building2;
            const isCurrentPlan = subscription?.plan_id === planId;
            const planInfo = PLAN_FEATURES[planId];
            
            return (
              <Card 
                key={planId} 
                className={`rounded-sm relative overflow-hidden ${isCurrentPlan ? 'border-2 border-orange-500' : ''}`}
                data-testid={`plan-card-${planId}`}
              >
                {isCurrentPlan && (
                  <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs px-3 py-1">
                    Current
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className={`h-12 w-12 ${planInfo?.color} rounded-sm flex items-center justify-center text-white mb-3`}>
                    <PlanIcon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{planInfo?.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">{formatPrice(plan.price)}</span>
                    <span className="text-muted-foreground">/month</span>
                    {plan.manager_addon && (
                      <p className="text-xs text-muted-foreground mt-1">
                        +{formatPrice(plan.manager_addon)}/additional manager
                      </p>
                    )}
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <ul className="space-y-2 mb-6">
                    {planInfo?.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Additional managers input for base plan */}
                  {planId === 'base' && !isCurrentPlan && (
                    <div className="mb-4 p-3 bg-secondary rounded-sm">
                      <label className="text-xs text-muted-foreground">Additional Managers</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdditionalManagers(Math.max(0, additionalManagers - 1))}
                          className="h-8 w-8 p-0"
                        >
                          -
                        </Button>
                        <span className="w-8 text-center font-semibold">{additionalManagers}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdditionalManagers(additionalManagers + 1)}
                          className="h-8 w-8 p-0"
                        >
                          +
                        </Button>
                        <span className="text-xs text-muted-foreground ml-2">
                          +{formatPrice(additionalManagers * (plan.manager_addon || 0))}
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    className={`w-full rounded-sm ${isCurrentPlan ? 'bg-slate-200 text-slate-600' : planInfo?.color + ' hover:opacity-90'}`}
                    disabled={isCurrentPlan || checkingOut !== null}
                    onClick={() => handleUpgrade(planId)}
                    data-testid={`upgrade-btn-${planId}`}
                  >
                    {checkingOut === planId ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      <>
                        Upgrade <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* FAQ */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-medium">Can I change my plan anytime?</p>
            <p className="text-sm text-muted-foreground">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <Separator />
          <div>
            <p className="font-medium">What happens when my trial ends?</p>
            <p className="text-sm text-muted-foreground">
              During your trial, you have full Business-level access. When it ends, your data is preserved, 
              but features will be limited based on your chosen plan. Subscribe before the trial ends to keep 
              all your team members and branding active!
            </p>
          </div>
          <Separator />
          <div>
            <p className="font-medium">How do I add more team members?</p>
            <p className="text-sm text-muted-foreground">
              On the Base plan, you can add managers at $9/month each. Pro includes 2 managers and 2 forepersons. Business has unlimited team members.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
